/**
 * Reads the inline brand template from window.__BRAND_TEMPLATE__
 *
 * In development / static builds, index.html contains only:
 *   { template: "classic" }
 *
 * In production (serve-index), it may contain the full BrandTemplate object
 * injected by the edge function at request time.
 */

// ── Full template interfaces (used by serve-index in production) ─────────────

export interface BrandTemplateTheme {
  primary_color: string;
  primary_color_hover: string;
  primary_color_light: string;
  secondary_color: string;
  accent_color: string;
  success_color: string;
  warning_color: string;
  error_color: string;
  background_color: string;
  background_color_dark: string;
  card_color: string;
  card_color_dark: string;
  text_color: string;
  text_color_dark: string;
  text_secondary: string;
  text_secondary_dark: string;
  border_color: string;
  border_color_dark: string;
  border_radius: string;
  border_radius_lg: string;
  border_radius_full: string;
}

export interface BrandTemplateBrand {
  id: string | null;
  name: string;
  name_ar: string;
  logo: string | null;
  logo_dark: string | null;
  logo_type: "emoji" | "image";
  logo_emoji: string | null;
  favicon: string | null;
  cover_image: string | null;
  tagline: string;
  tagline_ar: string;
  currency_code: string;
  currency_symbol: string;
  support_email: string;
  template: "classic" | "elegant" | "modern" | "landing" | "heritage";
  /** URL for the hero background video */
  hero_video_url?: string | null;
  whatsAppNumber?: string | null;
}


export interface BrandTemplateFonts {
  heading: string;
  heading_ar: string;
  body: string;
  body_ar: string;
  google_fonts_url: string | null;
}

export interface BrandTemplateLayout {
  header_style: string;
  header_height: string;
  menu_layout: string;
  menu_columns_desktop: number;
  menu_columns_tablet: number;
  menu_columns_mobile: number;
  category_style: string;
  category_style_mobile: string;
  card_style: string;
  card_image_ratio: string;
  show_item_rating: boolean;
  show_item_calories: boolean;
  show_dietary_badges: boolean;
  cart_style: string;
  footer_style: string;
}

/** Features configuration for the brand template. */
export interface BrandTemplateFeatures {
  dark_mode_enabled: boolean;
  dark_mode_default: boolean;
  language_toggle_enabled: boolean;
  default_language: string;
  supported_languages: string[];
  loyalty_enabled: boolean;
  wallet_enabled: boolean;
  vehicle_info_enabled: boolean;
  scheduled_orders_enabled: boolean;
  promo_codes_enabled: boolean;
  guest_checkout_enabled: boolean;
  social_login_enabled: boolean;
  order_tracking_enabled: boolean;
  notifications_enabled: boolean;
  productsAvailableInAllBranches: boolean;
  payment_method: string;
  auto_accept_order: boolean;
  payment_approval_mode: string;
  allow_coupon: boolean;
  order_minimum_allowed: number;
  has_schedule: boolean;
  is_delivery_active: boolean;
  is_pickup_active: boolean;
  is_dinein_active: boolean;
  vat_rate: number;
  tax_inclusive: boolean;
}

export interface BrandTemplateContent {
  hero_title: string;
  hero_title_ar: string;
  hero_subtitle: string;
  hero_subtitle_ar: string;
  meta_title: string;
  meta_title_ar: string;
  meta_description: string;
  meta_description_ar: string;
}

export interface BrandTemplateSocial {
  instagram: string | null;
  twitter: string | null;
  tiktok: string | null;
  facebook: string | null;
  whatsapp: string | null;
}

export interface BrandTemplateLegal {
  terms_url: string | null;
  privacy_url: string | null;
}

/** Full template object (production serve-index) */
export interface BrandTemplate {
  brand: BrandTemplateBrand;
  theme: BrandTemplateTheme;
  fonts: BrandTemplateFonts;
  layout: BrandTemplateLayout;
  features: BrandTemplateFeatures;
  content: BrandTemplateContent;
  social: BrandTemplateSocial;
  legal: BrandTemplateLegal;
  _version?: string;
  _generated_at?: string;
}

/** Minimal template object (dev/static index.html) */
export interface MinimalBrandTemplate {
  template: string;
}

declare global {
  interface Window {
    __BRAND_TEMPLATE__?: BrandTemplate | MinimalBrandTemplate;
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Check if the inline object is the full production template */
export function hasFullInlineTemplate(): boolean {
  try {
    const tpl = window.__BRAND_TEMPLATE__;
    return !!(tpl && typeof tpl === "object" && "brand" in tpl && "theme" in tpl);
  } catch {
    return false;
  }
}

/** @deprecated Use hasFullInlineTemplate or getInlineTemplateName */
export function hasInlineTemplate(): boolean {
  return hasFullInlineTemplate();
}

/**
 * Read only the template name from the inline object.
 * Works for both minimal { template: "classic" } and full objects.
 */
export function getInlineTemplateName(): "classic" | "elegant" | "modern" | "landing" | "heritage" {
  try {
    const tpl = window.__BRAND_TEMPLATE__;
    if (!tpl || typeof tpl !== "object") return "classic";
    // Full template: tpl.brand.template
    if ("brand" in tpl && tpl.brand?.template) return tpl.brand.template;
    // Minimal template: tpl.template
    if ("template" in tpl && tpl.template) return tpl.template as any;
    return "classic";
  } catch {
    return "classic";
  }
}

const FALLBACK: BrandTemplate = {
  brand: {
    id: null, name: "My Restaurant", name_ar: "", logo: null, logo_dark: null,
    logo_type: "emoji", logo_emoji: "🍔", favicon: null, cover_image: null,
    tagline: "Fast food, delivered fresh.", tagline_ar: "طعام سريع، يصل طازجاً.",
    currency_code: "USD", currency_symbol: "$", support_email: "", template: "classic",
  },
  theme: {
    primary_color: "#FF6B35", primary_color_hover: "#E55A2B", primary_color_light: "#FFF3ED",
    secondary_color: "#2A9D8F", accent_color: "#2A9D8F", success_color: "#22C55E",
    warning_color: "#F59E0B", error_color: "#EF4444", background_color: "#FAF8F5",
    background_color_dark: "#12122A", card_color: "#FFFFFF", card_color_dark: "#1E1E36",
    text_color: "#1A1A2E", text_color_dark: "#F3F4F6", text_secondary: "#6B7280",
    text_secondary_dark: "#9CA3AF", border_color: "#E5E7EB", border_color_dark: "#2D2D50",
    border_radius: "12px", border_radius_lg: "18px", border_radius_full: "9999px",
  },
  fonts: { heading: "Poppins", heading_ar: "Poppins", body: "Inter", body_ar: "Inter", google_fonts_url: null },
  layout: {
    header_style: "sticky", header_height: "60px", menu_layout: "grid",
    menu_columns_desktop: 3, menu_columns_tablet: 2, menu_columns_mobile: 1,
    category_style: "sidebar", category_style_mobile: "chips", card_style: "rounded",
    card_image_ratio: "4/3", show_item_rating: true, show_item_calories: false,
    show_dietary_badges: true, cart_style: "drawer", footer_style: "standard",
  },
  features: {
    dark_mode_enabled: true, dark_mode_default: false, language_toggle_enabled: true,
    default_language: "en", supported_languages: ["ar", "en"], loyalty_enabled: false,
    wallet_enabled: false, vehicle_info_enabled: false, scheduled_orders_enabled: false,
    promo_codes_enabled: true, guest_checkout_enabled: true, social_login_enabled: false,
    order_tracking_enabled: true, notifications_enabled: false, productsAvailableInAllBranches: false,
    payment_method: "card", auto_accept_order: false, payment_approval_mode: "auto",
    allow_coupon: true, order_minimum_allowed: 0, has_schedule: false,
    is_delivery_active: true, is_pickup_active: true, is_dinein_active: false, vat_rate: 0, tax_inclusive: false,
  },
  content: { hero_title: "", hero_title_ar: "", hero_subtitle: "", hero_subtitle_ar: "",
    meta_title: "", meta_title_ar: "", meta_description: "", meta_description_ar: "" },
  social: { instagram: null, twitter: null, tiktok: null, facebook: null, whatsapp: null },
  legal: { terms_url: null, privacy_url: null },
};

/** Get the full brand template. Returns FALLBACK if only minimal is present. */
export function getBrandTemplate(): BrandTemplate {
  try {
    const tpl = window.__BRAND_TEMPLATE__;
    if (tpl && typeof tpl === "object" && "brand" in tpl && "theme" in tpl) return tpl as BrandTemplate;
    return FALLBACK;
  } catch {
    return FALLBACK;
  }
}

export function getTheme(): BrandTemplateTheme { return getBrandTemplate().theme; }
export function getBrand(): BrandTemplateBrand { return getBrandTemplate().brand; }
export function getFeatures(): BrandTemplateFeatures { return getBrandTemplate().features; }
export function getLayout(): BrandTemplateLayout { return getBrandTemplate().layout; }
export function getFonts(): BrandTemplateFonts { return getBrandTemplate().fonts; }
export function getContent(): BrandTemplateContent { return getBrandTemplate().content; }
